import { useState } from "react";

import { AlertV4, ButtonV4, ModalV4, TabsV4 } from "../common/ui";
import { optionText } from "../common/displayTextV4";

import {
  ageRangeText,
  detailGroups,
  genderOf,
  lifespanText,
  priceText,
  quantityText,
  type MasterOption,
  type PackageDetail,
  type PackageItem,
} from "./packageModelV4";

// Types
type DetailTab = "items" | "info";

type ModalPackageDetailV4Props = {
  // data
  item: PackageItem;
  /** รายละเอียดของ item นี้ (null = ยังไม่มา) */
  detail: PackageDetail | null;
  loading: boolean;
  error?: unknown;
  serviceTypes: MasterOption[];
  packageTypes: MasterOption[];
  // callback
  onClose: () => void;
};

// Const
const DETAIL_TABS: { key: DetailTab; label: string }[] = [
  { key: "items", label: "รายการในแพ็กเกจ" },
  { key: "info", label: "ข้อมูลเพิ่มเติม" },
];

const ModalPackageDetailV4 = (props: ModalPackageDetailV4Props) => {
  const { item, packageTypes } = props;

  const [tab, setTab] = useState<DetailTab>("items");

  const groups = detailGroups(props.detail);
  const infoRows = [
    ["รหัส", item.code],
    ["ชนิด", optionText(packageTypes, item.package_type) || "-"],
    ["ราคา", `${priceText(item.price)} บาท`],
    ["ระยะเวลา", lifespanText(item)],
    ["เพศ", genderOf(item.gender).text],
    ["ช่วงอายุ", `${ageRangeText(item)} ปี`],
    ["ประเภทบริการ", optionText(props.serviceTypes, item.package_service_type) || "-"],
    ["ใช้ร่วมกันได้", "-"],
  ];

  // Render
  const renderItems = () => (
    <>
      <div className="hv4-pkg-drow hv4-pkg-dhead">
        <span>รายการในแพ็กเกจ</span>
        <span>จำนวนสิทธิ์</span>
        <span>หน่วย</span>
      </div>
      <AlertV4 tone="danger" error={props.error} />
      {props.loading ? (
        <div className="hv4-pkg-dempty">กำลังโหลด</div>
      ) : groups.length === 0 ? (
        <div className="hv4-pkg-dempty">
          แผนนี้ได้ราคาสมาชิกและเครดิตตามที่กำหนด ไม่มีรายการสิทธิ์เพิ่มเติม
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.category} className="hv4-pkg-dcat">
            <div className="hv4-pkg-dcat-title">
              <b>{group.category}</b> <span>({group.items.length} รายการ)</span>
            </div>
            {group.items.map((row) => (
              <div key={row.id} className="hv4-pkg-drow">
                <span className="hv4-pkg-dname">
                  <b>{row.product_name}</b>
                  <small>
                    {[row.product_code, row.product_name_en].filter(Boolean).join(" · ")}
                  </small>
                </span>
                <span>{quantityText(row.quantity)}</span>
                <span>{row.unit_name}</span>
              </div>
            ))}
          </div>
        ))
      )}
    </>
  );

  const renderInfo = () => (
    <div className="hv4-pkg-info">
      {infoRows.map(([key, value]) => (
        <div key={key} className="hv4-pkg-info-row">
          <span>{key}</span>
          <b>{value}</b>
        </div>
      ))}
    </div>
  );

  return (
    <ModalV4 title={item.name_en || item.name} width={980} onClose={props.onClose}>
      <div className="hv4-pkg-modal">
        <div className="hv4-pkg-modal-sub">
          {[item.name, item.code, optionText(packageTypes, item.package_type)]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <TabsV4<DetailTab>
          ariaLabel="รายละเอียดแพ็กเกจ"
          items={DETAIL_TABS}
          active={tab}
          onChange={setTab}
        />
        <div className="hv4-pkg-modal-body">{tab === "items" ? renderItems() : renderInfo()}</div>
        <div className="hv4-pkg-modal-foot">
          <ButtonV4 onClick={props.onClose}>ปิดหน้าต่าง</ButtonV4>
        </div>
      </div>
    </ModalV4>
  );
};

ModalPackageDetailV4.displayName = "ModalPackageDetailV4";

export default ModalPackageDetailV4;
